import { createSlice } from "@reduxjs/toolkit";
import { uiReducers } from "./ui";
import toolIndex from "toolConstants";
import { keys, values } from "lodash";
import classroomSlice from "./classroom";
import roomSlice from "./room";
import { warehouseReducers } from "./warehouseReducers";
import { roofReducers } from "./roofReducers";
import { dimensionsReducers } from "./dimensionsReducers";
import { acousticsReducers } from "./acousticsReducers";
import { deviceConfigReducers } from "./deviceConfigReducers";
import { areaReducers } from "./areaReducers";

// This initial state is preloaded for warehouse
const initialState = {
  currentTool: "",
  activeTab: "",
  currentDevice: "",
  // showMap: 'directSPL',
  project: {
    name: "",
    company: "",
    designer: "",
    locationId: "",
  },
  name: "Warehouse",
  dimensions: {
    length: 6.096,  // 20 feet - reasonable default for conference room
    width: 4.2672,  // 14 feet - reasonable default for conference room
  },

  // loading docks 
  loadingDocks: {
    top_docks: false,
    right_docks: false,
    bottom_docks: false,
    left_docks: true,
  },
  travelLane: { width: 9.7536 },
  ghostDocks: [],

  // using this to update/delete docks at the moment
  areaToUpdate: null,
  // delete: false,

  // roof
  roofType: "slope",
  roofSlopeOrientation: "slopeBottom",
  roofHeight: {
    min: 7.0104,
    max: 8.9408,
  },
  roofGrid: false,
  roofGridSpacing: { x: 2.4384, y: 1.2192 },
  roofGridOffset: { dx: 0, dy: 0 },

  // materials
  materials: {
    walls: 'CMU_Unpainted',
    ceiling: 'Metal_Deck',
  },

  budget: 'Standard',
  areas: {} as Record<string, any>,
  // recalculate: false,
  // remap: false,

  requestedQuote: false,
};
/**
 * Container Slice for managing the overall container state in the Redux store.
 * @type {import("@reduxjs/toolkit").Slice}
 */
const containerSlice = createSlice({
  name: "container",
  initialState: { ...initialState },
  reducers: {
    /**
     * Sets the project data, including areas and container properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The project data to set.
     * @param {Object} [action.payload.areas] - The areas data to set.
     */
    setProjectData: (state, action) => {
      const { areas = {}, ...container } = action.payload || {};
      // Copy container properties to state, excluding 'areas'
      // Preserve project name if it was set by conference room designer
      const preserveProjectName = state.currentTool === 'conferenceroomDesigner' && 
                                  state.project.name === 'Conference Room' && 
                                  (!container.project || !container.project.name);
      
      Object.entries(container).forEach(([key, value]) => {
        if (key !== 'areas') {
          if (key === 'project' && preserveProjectName) {
            // Preserve the existing project name but update other project properties
            const projectValue = value && typeof value === 'object' ? value : {};
            (state as any)[key] = { ...state.project, ...projectValue, name: state.project.name };
          } else {
            (state as any)[key] = value;
          }
        }
      });
      let newAreas = {}
      values(areas).forEach(area => {
        const { extraLoadParameters = {} } = toolIndex[state.currentTool].constants;
        const newArea = { ...toolIndex[state.currentTool].initialState.area(state), ...extraLoadParameters(area) };
        const assembledArea = { ...newArea, ...area };

        // Eagerly migrate legacy mic model keys on load so all downstream code
        // (roomCalculations, middleware, selectors) sees product keys immediately,
        // not just after the user touches the mic selector.
        const micConfigs = toolIndex[state.currentTool].constants.equipment?.microphones;
        if (micConfigs && assembledArea.microphones?.model) {
          const model = assembledArea.microphones.model;
          if (!micConfigs[model]) {
            const modelName = assembledArea.microphones.modelName;
            const resolved = modelName
              ? Object.keys(micConfigs).find(k => micConfigs[k].modelName === modelName)
              : undefined;
            if (resolved) {
              assembledArea.microphones = { ...assembledArea.microphones, model: resolved };
            } else {
              console.warn(`[setProjectData] Could not migrate mic model "${model}" (modelName: "${modelName}") — preserving original key`);
            }
          }
        }

        // Correct a saved coverageDensity.value that has drifted from the actual persisted
        // device count (e.g. a density-slider change that got saved before the layout
        // resynced, or a legacy import). The saved layout/qty is the ground truth for what
        // actually renders; if coverageDensity.value disagrees with it, a later placement
        // recalculation would read the stale value and silently regenerate a layout with a
        // different device count than what was saved (see PIG-604). Only correct when the
        // actual count is itself an achievable jump point — otherwise something deeper is
        // inconsistent and it's safer to leave the value alone for the normal recalculation
        // path to resolve.
        const reconcileCoverageDensity = (device: any): any => {
          const jumpPoints = device?.coverageDensity?.jumpPoints;
          if (!device?.coverageDensity || typeof device.coverageDensity.value !== 'number' || !Array.isArray(jumpPoints)) {
            return device;
          }
          const actualQty = Array.isArray(device.layout) && device.layout.length > 0
            ? device.layout.length
            : device.qty;
          if (!actualQty || device.coverageDensity.value === actualQty || !jumpPoints.includes(actualQty)) {
            return device;
          }
          console.warn(`[setProjectData] coverageDensity.value (${device.coverageDensity.value}) did not match the saved device count (${actualQty}) for area "${area.id}" — correcting to ${actualQty}.`);
          return { ...device, coverageDensity: { ...device.coverageDensity, value: actualQty } };
        };

        if (assembledArea.microphones) {
          assembledArea.microphones = reconcileCoverageDensity(assembledArea.microphones);
        }
        if (assembledArea.speakers) {
          assembledArea.speakers = reconcileCoverageDensity(assembledArea.speakers);
        }
        if (assembledArea.expansionMicrophones) {
          assembledArea.expansionMicrophones = reconcileCoverageDensity(assembledArea.expansionMicrophones);
        }

        newAreas = {
          // ...state.areas,
          ...newAreas,
          [area.id]: assembledArea,
        };
      });
      state.areas = { ...newAreas };
      const { tool: { localCalc, landingMenu: device, containerName: hasContainer } } = toolIndex[state.currentTool].constants;
      state.activeTab = hasContainer ? `_${hasContainer.toLowerCase()}` : Object.keys(state.areas)[0];
      if (localCalc) {
        state.currentDevice = device
      };
    },

    /**
     * Adds a new area to the container.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} [action.payload] - Optional payload for area data.
     * @param {string} [action.payload.areaToAdd] - The ID of the area template to use.
     */
    addArea: (state, action) => {
      const baseName = toolIndex[state.currentTool].constants.tool.areaName;
      const baseId = keys(state.areas).reduce((acc, curr) => {
        const match = curr.match(/\d+/g);
        const num = match ? parseInt(match[0], 10) : 0;
        return Math.max(num, acc);
      }, 0) + 1;
      const areaName = `${baseName} ${baseId}`
      const newId = `_${baseName.toLowerCase()}${baseId}`;
      const areaToAdd = action?.payload?.areaToAdd;
      const { id = newId, name = areaName, areaType = null } = state[areaToAdd] || {};
      const newArea = { ...toolIndex[state.currentTool].initialState.area(state) };
      newArea.id = id;
      newArea.name = name;
      if (areaType) newArea.areaType = areaType;
      if (toolIndex[state.currentTool].constants.tool.containerName) toolIndex[state.currentTool].setArea(state, newArea);
      state.areas = { ...state.areas, [id]: newArea };
      state.activeTab = newId;
      // state.areaToUpdate = null;
    },

    /**
     * Deletes an area from the container.
     * @param {Object} state - The current state.
     */
    deleteArea: (state, action) => {
      const areaToDeleteId = state.areaToUpdate?.id;
      if (!areaToDeleteId || typeof areaToDeleteId !== 'string') return;
      
      const { [areaToDeleteId]: deleted, ...areas } = state.areas as any;
      state.areas = { ...areas };
      if (deleted?.areaType === 'dock' && areaToDeleteId in state.loadingDocks) {
        containerSlice.caseReducers.setLoadingDocks(state, { ...action, payload: areaToDeleteId });
      };
      state.areaToUpdate = null;
      return;
    },

    /**
     * Sets the background noise level.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string|number} action.payload - The new background noise level or profile.
     */
    setBackgroundNoise: (state, action) => {
      containerSlice.caseReducers.setNoiseLevel(state, action);
    },

    /**
     * Append room slice and classroom slice
     */
    ...roomSlice.reducers,
    ...classroomSlice.reducers,
    ...roofReducers,
    ...dimensionsReducers,
    ...acousticsReducers,
    ...deviceConfigReducers,
    ...areaReducers,
    ...warehouseReducers,



  },

  // when a ui reducder is called i.e. setTool, this builder catches it abd does something locally 
  extraReducers: (builder) => {
    builder
      .addCase(uiReducers.setTool, (state, action) => {
        const tool = action.payload;
        const noContainer = !toolIndex[tool].constants.tool.containerName;
        if (state.currentTool !== tool) { // did this to prevent double trigger
          state.currentTool = tool;
          
          // Set default project name and designer for conference room designer
          if (tool === 'conferenceRoomDesigner') {
            if (!state.project.name) {
              state.project.name = 'Conference Room';
            }
            if (!state.project.designer) {
              const currentUsername = sessionStorage.getItem('username') || '';
              state.project.designer = currentUsername;
            }
          }
          
          if (noContainer) {
            // todo: need to find a way to set initial state to container on load
            state.name = null
            state.dimensions = null
            state.loadingDocks = null
            state.travelLane = null
            state.ghostDocks = null
            state.roofType = null
            state.roofSlopeOrientation = null
            state.roofHeight = null
            state.roofGrid = null
            state.roofGridSpacing = null
            state.roofGridOffset = null


            containerSlice.caseReducers.addArea(state, { type: 'addArea', payload: undefined })


          };
        };
      })

      .addCase(uiReducers.setActiveTab, (state, action) => {
        state.activeTab = action.payload;
      })

      .addCase(uiReducers.setCurrentView, (state, action) => {
        const { devices = [] } = toolIndex[state.currentTool].constants;
        // Don't override currentDevice for acoustics view - let UI reducer handle it
        if (action.payload === 'acoustics') {
          return; // Let UI reducer's acoustics logic handle currentDevice
        }
        
        // Preserve both currentDevice and showMap to maintain heat maps when navigating between sections
        if (devices.includes(action.payload)) {
          // Only change currentDevice if we're explicitly switching to a different device section
          const newDevice = action.payload;
          /**
           * @note: The current implementation of CRD uses a single camera so the menu item is 'camera' while the object is ready to support 
           * multiple cameras. Once that feature is enables and the menu item (label) changes to 'cameras' this override will not be necesary 
           */
          state.currentDevice = `${newDevice}${newDevice === 'camera' ? 's' : ''}`;
        }
        // For other non-device sections, preserve the current device and showMap to keep heat maps visible
      })

  },
});

export const containerReducers = { ...containerSlice.actions };
export default containerSlice.reducer;

// Selectors — extracted to containerSelectors.ts for maintainability.
// NOTE: Use explicit import-then-export (not "export { X } from 'Y'") so webpack can
// statically resolve these exports even when container.ts is inside a circular dependency.
import {
  getActiveTab,
  getAreaList,
  getArea,
  getRecalculate,
  getRemap,
  getAvailableArea,
  getAreaState,
  getAreaProperty,
  getAreaProperty2,
  getCurrentValue,
  getCurrentValue2,
  getConditionResult,
  getAvailableActions,
  getAreas,
  getAreaArray,
  getAreaBoxes,
  getAreaGrid,
  getAreaFineGrid,
  getOuterBox,
  getPlotProps,
  getPlotData,
  getRelativeAveragePlotData,
  getEquipmentSummary,
  getEquipmentList,
  getRequestFromData,
} from 'reduxModules/selectors/containerSelectors';
export {
  getActiveTab,
  getAreaList,
  getArea,
  getRecalculate,
  getRemap,
  getAvailableArea,
  getAreaState,
  getAreaProperty,
  getAreaProperty2,
  getCurrentValue,
  getCurrentValue2,
  getConditionResult,
  getAvailableActions,
  getAreas,
  getAreaArray,
  getAreaBoxes,
  getAreaGrid,
  getAreaFineGrid,
  getOuterBox,
  getPlotProps,
  getPlotData,
  getRelativeAveragePlotData,
  getEquipmentSummary,
  getEquipmentList,
  getRequestFromData,
};
